import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import './Cards.css';
import { FaLinkedin, FaFacebook, FaInstagram, FaYoutube, FaTwitter, FaGithub, FaPhoneAlt, FaEnvelope, FaVideo, FaMapMarkerAlt, FaGlobe } from 'react-icons/fa';
import { BsMicrosoftTeams } from 'react-icons/bs';

const Cards = () => {
    const [cards, setCards] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const navigate = useNavigate();
    const API_URL = process.env.REACT_APP_API_URL;
    const [userTier, setUserTier] = useState(null); 
    const [loadingTier, setLoadingTier] = useState(true);
    
    useEffect(() => {
        const token = localStorage.getItem('token');
        
        // 🛡️ Single Guard: Route protection for the entire page view
        if (!token) {
            navigate('/login');
            return;
        }

        const initializeDashboard = async () => {
            try {
                // 🚀 Concurrent Processing: Fires both API requests at the exact same time
                const [profileRes, cardsRes] = await Promise.all([
                    axios.get(`${API_URL}/api/users/profile`, {
                        headers: { Authorization: `Bearer ${token}` }
                    }),
                    axios.get(`${API_URL}/api/cards`, {
                        headers: { Authorization: `Bearer ${token}` }
                    })
                ]);

                // 1. Hydrate User Tier context
                setUserTier(profileRes.data.tier);

                // 2. Hydrate Cards array dataset
                setCards(Array.isArray(cardsRes.data) ? cardsRes.data : []);

            } catch (error) {
                console.error('Dashboard synchronization failure:', error);

                // 💡 Unified Security Check: If EITHER endpoint drops a 401, evict the session immediately
                if (error.response && error.response.status === 401) {
                    console.warn('Session token expired or invalid. Evicting user context.');
                    localStorage.removeItem('token');
                    navigate('/login');
                    return; // Stop execution here
                }

                // Fallback strategy for non-auth errors (e.g., standard network/server issues)
                setUserTier('free');
                
            } finally {
                // Always terminate the loading skeletons regardless of request success/failure
                setLoadingTier(false);
            }
        };

        initializeDashboard();
    }, [API_URL, navigate]);

    // ⏳ 3. Loading Guard: Prevents the screen flashing 'free' while the API resolves
    if (loadingTier) {
        return (
            <div className="analytics-container loading-page">
                <h2>Verifying Account Status...</h2>
            </div>
        );
    }

    // 🚀 2. Updated conditional creation check based on DB userTier
    const handleNewCard = () => {
        const isFreeTier = userTier === 'free' || !userTier;
        const isProTier = userTier === 'pro';

        // If they are free AND already have 2 or more cards, block them
        if (isFreeTier && cards.length >= 2) {
            setShowModal(true); // Open the upgrade modal pop-up
        
        } else if (isProTier && cards.length >= 5) {
            setShowModal(true); // Open the upgrade modal pop-up for Pro users with 5+ cards
        }

        else {
            navigate('/new-card'); // Premium users or free users under cap pass right through
        }
    };

    const handleEditCard = (id) => {
        navigate(`/edit-card/${id}`);
    };

    const renderIcons = (card) => {
        const icons = [
            { name: 'linkedin', url: card.linkedin, icon: <FaLinkedin />, show: !!card.linkedin },
            { name: 'facebook', url: card.facebook, icon: <FaFacebook />, show: !!card.facebook },
            { name: 'instagram', url: card.instagram, icon: <FaInstagram />, show: !!card.instagram },
            { name: 'youtube', url: card.youtube, icon: <FaYoutube />, show: !!card.youtube },
            { name: 'twitter', url: card.twitter, icon: <FaTwitter />, show: !!card.twitter },
            { name: 'teams', url: card.teams, icon: <BsMicrosoftTeams />, show: !!card.teams },
            { name: 'zoom', url: card.zoom, icon: <FaVideo />, show: !!card.zoom },
            { name: 'github', url: card.github, icon: <FaGithub />, show: !!card.github }
        ];

        return icons
            .filter(iconObj => iconObj.show)
            .map(({ name, url, icon }) => (
                <a key={name} href={url} target="_blank" rel="noopener noreferrer" className="icon">
                    {icon}
                </a>
            ));
    };

    return (
        <div className="cards-page-content">
            <h2>Your Business Cards</h2>
            <div className="cards-list">
                {Array.isArray(cards) && cards.map(card => {
                    const socialIcons = renderIcons(card);
                    return (
                        <div
                            key={card.id}
                            className="card-item"
                            onClick={() => handleEditCard(card.id)}
                        >
                            <div
                                className="card-background-cards"
                                style={{
                                    backgroundColor: card.backgroundColor,
                                    backgroundImage: card.backgroundImage ? `url(${card.backgroundImage})` : 'none',
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                }}
                            ></div>
                            <div className="card-details-cards">
                                <div className="profile-header-dashboard">
                                    <h3>{card.name || "Untitled Profile"}</h3>
                                    <p className="position-text-dashboard">{card.position}</p>
                                    {card.companyName && <p className="company-text-dashboard">{card.companyName}</p>}
                                </div>

                                <hr className="card-divider-dashboard" />

                                <div className="contact-rows-dashboard">
                                    {card.phone && (
                                        <div className="contact-row-item-dashboard">
                                            <span className="contact-icon-wrapper-dashboard"><FaPhoneAlt /></span>
                                            <span>{card.phone}</span>
                                        </div>
                                    )}
                                    {card.email && (
                                        <div className="contact-row-item-dashboard">
                                            <span className="contact-icon-wrapper-dashboard"><FaEnvelope /></span>
                                            <span className="truncate-text">{card.email}</span>
                                        </div>
                                    )}
                                    {card.address && (
                                        <div className="contact-row-item-dashboard">
                                            <span className="contact-icon-wrapper-dashboard"><FaMapMarkerAlt /></span>
                                            <span className="truncate-text">{card.address}</span>
                                        </div>
                                    )}
                                    {card.website && (
                                        <div className="contact-row-item-dashboard">
                                            <span className="contact-icon-wrapper-dashboard"><FaGlobe /></span>
                                            <span className="truncate-text">{card.website}</span>
                                        </div>
                                    )}
                                </div>

                                {socialIcons.length > 0 && <hr className="card-divider-dashboard" />}

                                <div className="card-icons-social-footer">
                                    {socialIcons}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
            <button onClick={handleNewCard}>Create New Card</button>

            {showModal && (
                <div className="modal-overlay">
                    <div className="cards-paywall-box">
                        <span className="close-modal-x" onClick={() => setShowModal(false)}>×</span>
                        <div className="lock-icon">🎴</div>
                        
                        <h3>{userTier === 'pro' ? 'Pro Plan Limit Reached' : 'Card Limit Reached'}</h3>
                        <p>
                            {userTier === 'pro' 
                                ? 'Pro accounts are limited to a maximum of 5 digital business card profiles. Upgrade to our Business Plan for unlimited card creation and advanced team management features.' 
                                : 'Free accounts are limited to a maximum of 2 digital business card profiles. Upgrade to a premium plan to unlock more custom layouts and rich networking analytics.'
                            }
                        </p>
                        
                        <div className="modal-button-group">
                            <button onClick={() => navigate('/pricing')} className="upgrade-cta-button">
                                {userTier === 'pro' ? 'View Business Plans' : 'View Premium Plans'}
                            </button>
                            <button onClick={() => setShowModal(false)} className="modal-cancel-btn">
                                Maybe Later
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Cards;

